import { useState, useMemo, useRef, type FormEvent, type ChangeEvent } from 'react'
import {
  type TitleOption,
  genderedTitleValue,
  titleSelectValue,
  visibleTitleOptions,
} from '@/lib/registrationFormShared'
import { Head, router } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ArrowLeft, Loader2, Upload } from 'lucide-react'
import PhoneInput, { type PhoneInputHandle } from '@/components/PhoneInput'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface RegistrationField {
  key: string
  order: number
  custom: boolean
}

interface CustomFieldDef {
  name: string
  nameEn: string
  fieldType: string
  required: boolean
  order: number
  options: string[]
  optionIconUrls?: Array<string | null>
}

interface EventUserData {
  eventId: number
  title: string | null
  otherTitle?: string | null
  firstName: string | null
  lastName: string | null
  phoneNumber: string | null
  email: string | null
  organization: string | null
  position: string | null
  seatRow: string | null
  seatColumn: number | null
  userType: string | null
  gender: string | null
  country: string | null
  city: string | null
  age: number | null
  idNumber: string | null
  passportNumber: string | null
  bloodType: string | null
  imageUrl: string | null
  customFields: Record<string, string>
  token?: string
}

interface Props extends Omit<AppShellProps, 'children'> {
  event: { id: number; name: string }
  eventUser: EventUserData
  registrationFields: RegistrationField[]
  customFields: CustomFieldDef[]
  userTypes: string[]
  bloodTypes: string[]
  genderOptions: string[]
  titleFieldType?: 'text' | 'select'
  titleOptions?: TitleOption[]
  positionFieldType?: 'text' | 'select'
  positionOptions?: string[]
  errors: Record<string, string>
  editing: boolean
  urls: AppShellProps['urls'] & {
    submit: string
    eventUsers: string
  }
  translations: AppShellProps['translations'] & {
    newUser: string
    editUser: string
    title: string
    otherTitle?: string
    firstName: string
    lastName: string
    phoneNumber: string
    invalidPhoneNumber: string
    email: string
    organization: string
    position: string
    seatRow: string
    seatColumn: string
    badgeType: string
    gender: string
    country: string
    city: string
    age: string
    idNumber: string
    passportNumber: string
    bloodType: string
    image: string
    pleaseSelect: string
    submit: string
    back: string
  }
}

/* ------------------------------------------------------------------ */
/*  Field map: registration_field.key → form field name               */
/* ------------------------------------------------------------------ */

const FIELD_MAP: Record<string, string> = {
  'Title': 'title',
  'First name': 'firstName',
  'Last name': 'lastName',
  'Phone number': 'phoneNumber',
  'Email': 'email',
  'Organization': 'organization',
  'Position': 'position',
  'Seat row': 'seatRow',
  'Seat column': 'seatColumn',
  'User type': 'userType',
  'Gender': 'gender',
  'Country': 'country',
  'City': 'city',
  'Age': 'age',
  'Id number': 'idNumber',
  'Passport number': 'passportNumber',
  'Blood type': 'bloodType',
  'Image': 'image',
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function NewAdmin(props: Props) {
  const { event, eventUser, registrationFields, customFields, userTypes, bloodTypes, genderOptions, titleFieldType = 'text', titleOptions = [], positionFieldType = 'text', positionOptions = [], errors, editing, urls, translations: t, locale, ...shell } = props

  const [form, setForm] = useState({
    title: eventUser.title || '',
    otherTitle: eventUser.otherTitle || '',
    firstName: eventUser.firstName || '',
    lastName: eventUser.lastName || '',
    phoneNumber: eventUser.phoneNumber || '',
    email: eventUser.email || '',
    organization: eventUser.organization || '',
    position: eventUser.position || '',
    seatRow: eventUser.seatRow || '',
    seatColumn: eventUser.seatColumn?.toString() || '',
    userType: eventUser.userType || '',
    gender: eventUser.gender || '',
    country: eventUser.country || '',
    city: eventUser.city || '',
    age: eventUser.age?.toString() || '',
    idNumber: eventUser.idNumber || '',
    passportNumber: eventUser.passportNumber || '',
    bloodType: eventUser.bloodType || '',
    customFields: { ...eventUser.customFields },
  })
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(eventUser.imageUrl)
  const [submitting, setSubmitting] = useState(false)
  const [phoneError, setPhoneError] = useState('')
  const phoneRef = useRef<PhoneInputHandle>(null)

  const visibleTitles = useMemo(
    () => visibleTitleOptions(titleOptions, locale),
    [titleOptions, locale],
  )

  const set = (key: string, value: string) =>
    setForm((current) => {
      if (key === 'gender') {
        return { ...current, gender: value, title: genderedTitleValue(visibleTitles, current.title, value) }
      }
      if (key === 'title') {
        return { ...current, title: genderedTitleValue(visibleTitles, value, current.gender) }
      }
      return { ...current, [key]: value }
    })
  const setCustom = (key: string, value: string) => setForm(f => ({ ...f, customFields: { ...f.customFields, [key]: value } }))

  const handleImage = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setImageFile(file)
      setImagePreview(URL.createObjectURL(file))
    }
  }

  const enabledFields = new Set(registrationFields.map(f => f.key))

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    let phoneNumber = form.phoneNumber
    if (enabledFields.has('Phone number')) {
      const phoneCheck = await phoneRef.current?.validatePhone({ required: false })
      if (phoneCheck && !phoneCheck.empty && !phoneCheck.valid) {
        setPhoneError(t.invalidPhoneNumber)
        return
      }
      setPhoneError('')
      if (phoneCheck?.number) phoneNumber = phoneCheck.number
    }

    setSubmitting(true)

    const data = new FormData()
    data.append('event_user[event_id]', event.id.toString())
    data.append('event_user[title]', form.title)
    if (form.otherTitle) data.append('event_user[other_title]', form.otherTitle)
    data.append('event_user[first_name]', form.firstName)
    data.append('event_user[last_name]', form.lastName)
    data.append('event_user[phone_number]', phoneNumber)
    data.append('event_user[email]', form.email)
    data.append('event_user[organization]', form.organization)
    data.append('event_user[position]', form.position)
    data.append('event_user[seat_row]', form.seatRow)
    data.append('event_user[seat_column]', form.seatColumn)
    data.append('event_user[user_type]', form.userType)
    data.append('event_user[gender]', form.gender)
    data.append('event_user[country]', form.country)
    data.append('event_user[city]', form.city)
    data.append('event_user[age]', form.age)
    data.append('event_user[id_number]', form.idNumber)
    data.append('event_user[passport_number]', form.passportNumber)
    data.append('event_user[blood_type]', form.bloodType)

    Object.entries(form.customFields).forEach(([k, v]) => {
      data.append(`event_user[custom_fields][${k}]`, v)
    })

    if (imageFile) {
      data.append('event_user[image]', imageFile)
    }

    router.post(urls.submit, data, {
      forceFormData: true,
      onFinish: () => setSubmitting(false),
    })
  }

  /* Map custom field defs by order for interspersing with standard fields */
  const customByOrder = new Map<number, CustomFieldDef[]>()
  customFields.forEach(cf => {
    const arr = customByOrder.get(cf.order) || []
    arr.push(cf)
    customByOrder.set(cf.order, arr)
  })

  const pageTitle = editing ? t.editUser : t.newUser

  /* Render a standard text/email/number input */
  const renderInput = (fieldKey: string, formKey: string, label: string, type = 'text', opts: { required?: boolean; pattern?: string } = {}) => (
    <div className="space-y-1.5" key={fieldKey}>
      <Label htmlFor={formKey} className="text-sm font-medium text-[#37352F]">{label}</Label>
      <Input
        id={formKey}
        type={type}
        value={(form as unknown as Record<string, string>)[formKey] || ''}
        onChange={e => set(formKey, e.target.value)}
        placeholder={label}
        required={opts.required}
        pattern={opts.pattern}
        className="border-[#E8E8E5] focus:border-[#2383E2] focus:ring-[#2383E2]"
      />
      {errors[formKey] && <p className="text-xs text-red-500">{errors[formKey]}</p>}
    </div>
  )

  /* Render title field (text or dropdown) */
  const renderTitleField = () => {
    if (titleFieldType === 'select' && titleOptions.length > 0) {
      return (
        <>
          <div className="space-y-1.5" key="Title">
            <Label htmlFor="title" className="text-sm font-medium text-[#37352F]">{t.title}</Label>
            <select
              id="title"
              value={titleSelectValue(visibleTitles, form.title)}
              onChange={e => set('title', e.target.value)}
              className="flex h-9 w-full rounded-md border border-[#E8E8E5] bg-transparent px-3 py-1 text-sm transition-colors focus:border-[#2383E2] focus:outline-none focus:ring-1 focus:ring-[#2383E2]"
            >
              <option value="">{t.pleaseSelect}</option>
              {visibleTitles.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {errors.title && <p className="text-xs text-red-500">{errors.title}</p>}
          </div>
          {form.title === 'other' && (
            <div className="space-y-1.5" key="otherTitle">
              <Label htmlFor="otherTitle" className="text-sm font-medium text-[#37352F]">{t.otherTitle || 'Specify title'}</Label>
              <Input
                id="otherTitle"
                value={form.otherTitle}
                onChange={e => set('otherTitle', e.target.value)}
                placeholder={t.pleaseSpecifyTitle || t.otherTitle || 'Please specify title'}
                className="border-[#E8E8E5] focus:border-[#2383E2] focus:ring-[#2383E2]"
              />
              {errors.otherTitle && <p className="text-xs text-red-500">{errors.otherTitle}</p>}
            </div>
          )}
        </>
      )
    }

    return renderInput('Title', 'title', t.title)
  }

  const renderPositionField = () => {
    if (positionFieldType === 'select' && positionOptions.length > 0) {
      return (
        <div className="space-y-1.5" key="Position">
          <Label htmlFor="position" className="text-sm font-medium text-[#37352F]">{t.position}</Label>
          <select
            id="position"
            value={form.position}
            onChange={e => set('position', e.target.value)}
            className="flex h-9 w-full rounded-md border border-[#E8E8E5] bg-transparent px-3 py-1 text-sm transition-colors focus:border-[#2383E2] focus:outline-none focus:ring-1 focus:ring-[#2383E2]"
          >
            <option value="">{t.pleaseSelect}</option>
            {positionOptions.map(option => <option key={option} value={option}>{option}</option>)}
          </select>
          {errors.position && <p className="text-xs text-red-500">{errors.position}</p>}
        </div>
      )
    }

    return renderInput('Position', 'position', t.position, 'text')
  }

  /* Render a select input */
  const renderSelect = (formKey: string, label: string, options: string[], required = false) => (
    <div className="space-y-1.5" key={formKey}>
      <Label htmlFor={formKey} className="text-sm font-medium text-[#37352F]">{label}</Label>
      <select
        id={formKey}
        value={(form as unknown as Record<string, string>)[formKey] || ''}
        onChange={e => set(formKey, e.target.value)}
        required={required}
        className="flex h-9 w-full rounded-md border border-[#E8E8E5] bg-transparent px-3 py-1 text-sm transition-colors focus:border-[#2383E2] focus:outline-none focus:ring-1 focus:ring-[#2383E2]"
      >
        <option value="">{t.pleaseSelect}</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      {errors[formKey] && <p className="text-xs text-red-500">{errors[formKey]}</p>}
    </div>
  )

  /* Render a custom field */
  const renderCustomField = (cf: CustomFieldDef) => {
    const cfKey = cf.nameEn || cf.name
    const val = form.customFields[cfKey] || ''
    switch (cf.fieldType) {
      case 'drop_down':
        return (
          <div className="space-y-1.5" key={`cf-${cfKey}`}>
            <Label className="text-sm font-medium text-[#37352F]">{cf.name}</Label>
            <select
              value={val}
              onChange={e => setCustom(cfKey, e.target.value)}
              required={cf.required}
              className="flex h-9 w-full rounded-md border border-[#E8E8E5] bg-transparent px-3 py-1 text-sm transition-colors focus:border-[#2383E2] focus:outline-none focus:ring-1 focus:ring-[#2383E2]"
            >
              <option value="">{t.pleaseSelect}</option>
              {cf.options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        )
      case 'radio_button':
        return (
          <fieldset className="space-y-2" key={`cf-${cfKey}`}>
            <legend className="text-sm font-medium text-[#37352F]">{cf.name}</legend>
            <div className="flex flex-col gap-2">
              {cf.options.map((o, optionIdx) => {
                const optionId = `cf-${cfKey}-${o.replace(/\s+/g, '-').toLowerCase()}`
                const iconUrl = cf.optionIconUrls?.[optionIdx]
                return (
                  <label key={`${optionIdx}-${o}`} htmlFor={optionId} className="flex items-center gap-2 text-sm text-[#37352F]">
                    <input
                      type="radio"
                      id={optionId}
                      name={`custom-${cfKey}`}
                      value={o}
                      checked={val === o}
                      onChange={() => setCustom(cfKey, o)}
                      required={cf.required && !val}
                      className="h-4 w-4 border-[#E8E8E5] text-[#2383E2]"
                    />
                    {iconUrl && <img src={iconUrl} alt="" className="h-6 w-6 object-contain" />}
                    <span>{o}</span>
                  </label>
                )
              })}
            </div>
          </fieldset>
        )
      case 'check_box':
        return (
          <div className="flex items-center gap-2" key={`cf-${cfKey}`}>
            <input
              type="checkbox"
              id={`cf-${cfKey}`}
              checked={val === '1' || val === 'true'}
              onChange={e => setCustom(cfKey, e.target.checked ? '1' : '0')}
              required={cf.required}
              className="h-4 w-4 rounded border-[#E8E8E5]"
            />
            <Label htmlFor={`cf-${cfKey}`} className="text-sm text-[#37352F]">
              {cf.name}{cf.required && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
          </div>
        )
      case 'text_area':
        return (
          <div className="space-y-1.5" key={`cf-${cfKey}`}>
            <Label className="text-sm font-medium text-[#37352F]">{cf.name}</Label>
            <textarea
              value={val}
              onChange={e => setCustom(cfKey, e.target.value)}
              required={cf.required}
              rows={3}
              className="flex w-full rounded-md border border-[#E8E8E5] bg-transparent px-3 py-2 text-sm transition-colors focus:border-[#2383E2] focus:outline-none focus:ring-1 focus:ring-[#2383E2]"
            />
          </div>
        )
      default: // text_field
        return (
          <div className="space-y-1.5" key={`cf-${cfKey}`}>
            <Label className="text-sm font-medium text-[#37352F]">{cf.name}</Label>
            <Input
              value={val}
              onChange={e => setCustom(cfKey, e.target.value)}
              required={cf.required}
              placeholder={cf.name}
              className="border-[#E8E8E5] focus:border-[#2383E2] focus:ring-[#2383E2]"
            />
          </div>
        )
    }
  }

  /* Build ordered field list */
  const renderFields = () => {
    const elements: React.ReactNode[] = []

    registrationFields.forEach(rf => {
      const fmKey = FIELD_MAP[rf.key]

      /* Standard fields */
      if (fmKey && !rf.custom) {
        switch (rf.key) {
          case 'Title': elements.push(renderTitleField()); break
          case 'First name': elements.push(renderInput('First name', 'firstName', t.firstName, 'text', { required: true })); break
          case 'Last name': elements.push(renderInput('Last name', 'lastName', t.lastName, 'text', { required: true })); break
          case 'Phone number': elements.push(
                <div className="space-y-1.5" key="Phone number">
                  <Label htmlFor="phoneNumber" className="text-sm font-medium text-[#37352F]">{t.phoneNumber}</Label>
                  <PhoneInput
                    ref={phoneRef}
                    value={form.phoneNumber}
                    onChangeNumber={(num) => { set('phoneNumber', num); if (phoneError) setPhoneError('') }}
                    inputProps={{
                      id: 'phoneNumber',
                      className: 'border-[#E8E8E5] focus:border-[#2383E2] focus:ring-[#2383E2]',
                    }}
                  />
                  {(phoneError || errors.phoneNumber) && <p className="text-xs text-red-500">{phoneError || errors.phoneNumber}</p>}
                </div>,
              ); break
          case 'Email': elements.push(renderInput('Email', 'email', t.email, 'email', { required: true })); break
          case 'Organization': elements.push(renderInput('Organization', 'organization', t.organization)); break
          case 'Position': elements.push(renderPositionField()); break
          case 'Seat row': elements.push(renderInput('Seat row', 'seatRow', t.seatRow)); break
          case 'Seat column': elements.push(renderInput('Seat column', 'seatColumn', t.seatColumn, 'number')); break
          case 'User type':
            if (userTypes.length > 0) {
              elements.push(renderSelect('userType', t.badgeType, userTypes, true))
            }
            break
          case 'Gender': elements.push(renderSelect('gender', t.gender, genderOptions, true)); break
          case 'Country': elements.push(renderInput('Country', 'country', t.country)); break
          case 'City': elements.push(renderInput('City', 'city', t.city)); break
          case 'Age': elements.push(renderInput('Age', 'age', t.age, 'text', { required: true, pattern: '^[1-9][0-9]?$|^100$' })); break
          case 'Id number': elements.push(renderInput('Id number', 'idNumber', t.idNumber, 'text', { required: true, pattern: '^[0-9]*$' })); break
          case 'Passport number': elements.push(renderInput('Passport number', 'passportNumber', t.passportNumber)); break
          case 'Blood type': elements.push(renderSelect('bloodType', t.bloodType, bloodTypes)); break
          case 'Image':
            elements.push(
              <div className="space-y-1.5" key="image">
                <Label htmlFor="image" className="text-sm font-medium text-[#37352F]">{t.image}</Label>
                <div className="flex items-center gap-3">
                  <label className="flex cursor-pointer items-center gap-2 rounded-md border border-[#E8E8E5] px-3 py-2 text-sm hover:bg-[#F1F1EF] transition-colors">
                    <Upload className="h-4 w-4 text-[#91918E]" />
                    <span className="text-[#37352F]">{imageFile?.name || t.image}</span>
                    <input type="file" accept="image/*" onChange={handleImage} className="hidden" />
                  </label>
                  {imagePreview && (
                    <img src={imagePreview} alt="Preview" className="h-16 w-16 rounded-md object-cover border border-[#E8E8E5]" />
                  )}
                </div>
              </div>
            )
            break
        }
      }

      /* Custom fields: only render when this registration field is a custom slot */
      if (rf.custom) {
        const customs = customByOrder.get(rf.order)
        if (customs) {
          customs.forEach(cf => elements.push(renderCustomField(cf)))
        }
      }
    })

    return elements
  }

  return (
    <AppShell locale={locale} urls={urls} translations={t} {...shell}>
      <Head title={`${pageTitle} — ${event.name}`} />

      <div className="mx-auto max-w-2xl px-4 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <a
            href={urls.eventUsers}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-[#F1F1EF] transition-colors"
          >
            <ArrowLeft className="h-4 w-4 text-[#91918E]" />
          </a>
          <div>
            <h1 className="text-xl font-semibold text-[#37352F]">{pageTitle}</h1>
            <p className="text-sm text-[#91918E]">{event.name}</p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="rounded-lg border border-[#E8E8E5] bg-white p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {renderFields()}
          </div>

          {Object.keys(errors).length > 0 && (
            <div className="mt-4 rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-600">
                {Object.values(errors).join(', ')}
              </p>
            </div>
          )}

          <div className="mt-6 flex justify-end gap-3">
            <a
              href={urls.eventUsers}
              className="inline-flex h-9 items-center justify-center rounded-md border border-[#E8E8E5] px-4 text-sm font-medium text-[#37352F] hover:bg-[#F1F1EF] transition-colors"
            >
              {t.back}
            </a>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#2383E2] text-white hover:bg-[#1b6abf]"
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t.submit}
            </Button>
          </div>
        </form>
      </div>
    </AppShell>
  )
}
